import { useState } from 'react';
import { Check, Mail, Loader2, AlertCircle } from 'lucide-react';
import { getSessionId } from '../../utils/sessionId';
import { requestMagicLink } from '../../services/auth/magicLinkService';
import { auth } from '../../services/firebase';

interface EmailSaveModalProps {
  onSubmit: (email: string | null) => void;
}

export function EmailSaveModal({ onSubmit }: EmailSaveModalProps) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const trimmed = email.trim();
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  // Skip if already authenticated with email
  const user = auth.currentUser;
  if (user && !user.isAnonymous && user.email) {
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }
    if (!isValid) {
      setError('Please enter a valid email address.');
      return;
    }

    setSaving(true);
    try {
      const sessionId = getSessionId();
      const result = await requestMagicLink(trimmed.toLowerCase(), sessionId);

      if (result.success) {
        setSaved(true);
      } else {
        setError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('[EmailSaveModal] Failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (saved) {
    return (
      <div className="bg-white border border-zinc-200 rounded-[4px] p-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
            <Check size={14} className="text-white" aria-hidden="true" />
          </div>
          <p className="text-[14px] font-medium text-[#18181B]">Check your inbox</p>
        </div>
        <p className="text-zinc-500 text-[13px] leading-relaxed mb-3">
          We sent a link to <strong className="text-[#18181B]">{trimmed.toLowerCase()}</strong>.
          Click it anytime to return to your results — no password needed.
        </p>
        <button
          onClick={() => onSubmit(trimmed.toLowerCase())}
          className="w-full py-2.5 rounded-[4px] bg-zinc-100 border border-zinc-200 text-[#18181B] text-[14px] font-medium
            hover:bg-zinc-200 transition-colors"
        >
          Got it
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-[4px] p-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <p className="text-[14px] font-medium text-[#18181B] mb-1">Save your results</p>
      <p className="text-zinc-500 text-[13px] leading-relaxed mb-3">
        We'll email you a secure, passwordless link so you can return to your report and protocol anytime.
      </p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label htmlFor="email-save-input" className="text-[13px] font-medium text-zinc-600 block mb-1">
            Email address <span className="text-[#E11D48]">*</span>
          </label>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" aria-hidden="true" />
            <input
              id="email-save-input"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
              }}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={saving}
              aria-describedby={error ? 'email-save-error' : undefined}
              aria-invalid={error ? true : undefined}
              className={`w-full pl-10 pr-3 py-2.5 rounded-[4px] border text-[14px]
                font-['DM_Sans'] text-[#18181B] placeholder:text-zinc-400 outline-none
                focus:border-[#E11D48] focus:ring-2 focus:ring-[#E11D48]/20 transition-colors
                disabled:opacity-50 ${error ? 'border-red-400 bg-red-50/30' : 'border-zinc-200 bg-zinc-50'}`}
            />
          </div>
        </div>

        {error && (
          <div id="email-save-error" role="alert" className="flex items-start gap-2 text-[13px] text-red-600 bg-red-50 px-3 py-2 rounded-[4px] border border-red-100">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !trimmed}
          className="w-full py-2.5 rounded-[4px] text-white text-[14px] font-medium
            hover:bg-[#BE123C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed
            flex items-center justify-center gap-2"
          style={{ background: '#E11D48' }}
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Sending link...
            </>
          ) : (
            'Send me a return link'
          )}
        </button>
      </form>

      <button
        onClick={() => onSubmit(null)}
        disabled={saving}
        className="w-full mt-2 py-2 text-zinc-400 text-[13px] hover:text-zinc-600 transition-colors
          disabled:opacity-50"
      >
        Maybe later
      </button>
    </div>
  );
}
